import { useState } from 'react';
import { TENANT_STATUS_MAP } from '@goyone/shared';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from './Icon';
import { date, plural, WORDS } from '../lib/format';

const DAY = 86_400_000;

function daysUntil(value: string | null): number | null {
  if (!value) return null;
  return Math.ceil((new Date(value).getTime() - Date.now()) / DAY);
}

/**
 * حالة الاشتراك أمام المستخدم.
 *
 * الخادم يمنع الكتابة على الحساب الموقوف برسالة خطأ لا تظهر إلا عند محاولة
 * الحفظ. المستخدم حينها يظن أن النظام معطّل لا أن اشتراكه انتهى. اللافتة
 * تجعل السبب ظاهرًا قبل أن يصطدم به، وتترك له طريقًا للتصرّف.
 *
 * ولا تظهر بلا داعٍ: الحساب النشط الذي أمامه أسبوع أو أكثر لا يرى شيئًا،
 * فالتنبيه الدائم يُتجاهَل حين يصير عاديًا.
 */
export function SubscriptionBanner() {
  const { session } = useAuth();
  if (!session) return null;

  const { status, trialEndsAt, periodEndsAt, graceEndsAt, planName } = session.tenant;

  if (status === 'SUSPENDED') {
    return (
      <Notice
        kind="danger"
        title="الحساب موقوف"
        text="يمكنك الاطلاع على بياناتك دون تعديلها. تواصل معنا لتجديد الاشتراك واستئناف العمل."
      />
    );
  }

  if (status === 'CANCELLED') {
    return (
      <Notice
        kind="danger"
        title="الاشتراك ملغى"
        text="بياناتك محفوظة ويمكن الاطلاع عليها. تواصل معنا لإعادة تفعيل الحساب."
        /* حالةٌ ساكنةٌ بلا موعد — يُغلق ليومه ويعود غدًا. */
        dismissKey={`goyone.banner.cancelled.${today()}`}
      />
    );
  }

  if (status === 'PAST_DUE') {
    const left = daysUntil(graceEndsAt);
    return (
      <Notice
        kind="warning"
        title="اشتراكك بحاجة إلى تجديد"
        text={
          left !== null && left > 0
            ? `الحساب يعمل خلال مهلة سداد تنتهي بعد ${plural(left, WORDS.day)}. بعدها يتوقف عن قبول أي تعديل.`
            : 'مهلة السداد انتهت، وسيتوقف الحساب عن قبول التعديلات قريبًا.'
        }
        /*
         * يُغلق ليومه لا إلى الأبد.
         *
         * كان لا يُغلق أصلًا: شريطٌ يعلو كلَّ شاشةٍ ويأكل من ارتفاعها ولا
         * سبيل إلى تنحيته — وهو نفسُه في كلّ صفحة. لكنّ إغلاقًا دائمًا أسوأ:
         * «سيتوقف الحساب عن قبول التعديلات» خبرٌ لو نُسي فوجئ صاحبُه بحسابٍ
         * متوقّف. فالمفتاحُ يحمل اليومَ وعددَ ما بقي — يُغلق فيسكت اليوم،
         * ويعود غدًا برقمٍ أصغر. والإلحاحُ يشتدّ كلّما قرُب الموعد لا يخفت.
         */
        dismissKey={`goyone.banner.pastdue.${graceEndsAt ?? 'none'}.${left ?? 'x'}.${today()}`}
      />
    );
  }

  if (status === 'TRIAL') {
    const left = daysUntil(trialEndsAt);
    if (left === null) return null;

    /*
     * التنبيه يشتد كلما اقترب الانتهاء.
     *
     * لون واحد طوال التجربة يجعل اليوم الأخير كاليوم الأول، فلا يتحرك أحد
     * إلا بعد التوقف.
     */
    if (left > 7) return null;
    const kind = left <= 2 ? 'danger' : 'warning';
    return (
      <Notice
        kind={kind}
        title={
          left <= 0
            ? 'انتهت الفترة التجريبية'
            : `تبقّى من التجربة ${plural(left, WORDS.day)}`
        }
        text={
          left <= 0
            ? 'تواصل معنا لتفعيل اشتراكك ومواصلة العمل ببياناتك كما هي.'
            : `${planName ? `باقة ${planName} — ` : ''}تنتهي في ${date(trialEndsAt)}. تواصل معنا للتفعيل قبل ذلك.`
        }
        /*
         * تنبيهٌ يُغلق: التذكير بالتجربة نافعٌ لا حاجز، فإغلاقه حقٌّ للمستخدم.
         * والمفتاح يحمل التاريخ والحدّة، فإغلاقُ «٧ أيام» لا يُخفي «يومان» حين
         * يشتدّ اللون — يعود التنبيه مرّةً عند كل تصعيد.
         */
        dismissKey={`goyone.banner.trial.${trialEndsAt}.${kind}`}
      />
    );
  }

  // النشط: لا تنبيه إلا في الأسبوع الأخير من مدته.
  const left = daysUntil(periodEndsAt);
  if (left !== null && left <= 7 && left >= 0) {
    return (
      <Notice
        kind="info"
        title={`اشتراكك يُجدَّد بعد ${plural(left, WORDS.day)}`}
        text={`${planName ? `باقة ${planName} — ` : ''}تنتهي المدة الحالية في ${date(periodEndsAt)}.`}
        dismissKey={`goyone.banner.renew.${periodEndsAt}`}
      />
    );
  }

  return null;
}

/** تاريخُ اليوم مفتاحًا: «٢٠٢٦-٠٩-٠٦» — به يعود التنبيهُ في اليوم التالي. */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function Notice({
  kind,
  title,
  text,
  dismissKey,
}: {
  kind: 'info' | 'warning' | 'danger';
  title: string;
  text: string;
  /** حين يُمرَّر، يظهر زرّ إغلاقٍ ويُحفظ الإغلاق في المتصفّح بهذا المفتاح. */
  dismissKey?: string;
}) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!dismissKey) return false;
    try {
      return localStorage.getItem(dismissKey) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  return (
    <div className={`sub-banner is-${kind}`}>
      <Icon name={kind === 'info' ? 'zap' : 'alert'} size={17} />
      <div className="col" style={{ gap: 1 }}>
        <span className="strong">{title}</span>
        <span className="small">{text}</span>
      </div>
      {dismissKey && (
        <button
          type="button"
          className="sub-banner-close"
          aria-label="إغلاق التنبيه"
          title="إغلاق"
          onClick={() => {
            try {
              localStorage.setItem(dismissKey, '1');
            } catch {
              /* يُغلق هذه الجلسة ولو تعذّر الحفظ. */
            }
            setDismissed(true);
          }}
        >
          <Icon name="x" size={16} />
        </button>
      )}
    </div>
  );
}

/** يُستعمل في القوائم للتحقق قبل عرض أزرار الإضافة. */
export function useCanWrite(): boolean {
  const { session } = useAuth();
  if (!session) return false;
  return TENANT_STATUS_MAP[session.tenant.status].access === 'full';
}
